import { Button, Modal } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IProcessAction } from '@common/interfaces';
import { ProcessActivityStatus } from '@common/enums';
import { holterStepNames } from '@common/utils/serviceWorkflowStepNames/holterStepNames';
import { sleep } from '@common/utils/sleep';
import { createHolterDiaryForProcedure } from '@frontend/services/api/procedureApiService';
import { getOneProcessActivity } from '@frontend/services/api/processActivitiesApiService';
import { createHolterDiaryForProcess, getAvailableActions } from '@frontend/services/api/processesApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { RecorderType } from '../../../../common/enums/recorderType';
import type { IDiaryData } from '../../../../common/validation/holterDiary';
import type { ValidationErrors } from '../../../utils/validation';
import { DiaryTypes } from '../../../../common/enums/diaryTypes';
import { diarySchema } from '../../../../common/validation/holterDiary';
import { fetchJson } from '../../../store/fetch';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { HolterDiaryUpload } from './HolterDiaryUpload';
import { HolterReadDownloadProgress } from './HolterReadDownloadProgress';

type HolterReadModalProps = {
    downloadProgress?: number;
    isDiaryUpload?: boolean;
    onClose: () => Promise<void>;
    processActivityId?: string | null;
    processId?: string | null;
    recorderType: RecorderType;
    requestId?: string | null;
    showDownloadProgress?: boolean;
};

export function HolterReadAndUploadDairyModel(props: Readonly<HolterReadModalProps>) {
    const abortSignal = useAbortSignal();
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [diaryData, setDiaryData] = useState<IDiaryData>({ assets: [], diaryType: undefined });
    const [diaryCreationFailed, setDiaryCreationFailed] = useState(false);
    const [diarySelectionAlreadyMade, setDiarySelectionAlreadyMade] = useState(false);

    useEffect(() => {
        if (props.requestId) {
            checkDiarySelectionRequestId(props.requestId);
        }
    }, [props.requestId]);

    useEffect(() => {
        if (props.processActivityId) {
            diaryUploadActivity({ processActivityId: props.processActivityId });
        }
    }, [props.processActivityId]);

    useEffect(() => {
        if (props.processId) {
            diaryUploadActivity({ processId: props.processId });
        }
    }, [props.processId]);

    return (
        <Modal
            isOpen
            onClose={props.onClose}
            size="medium"
        >
            <Modal.Header>{props.isDiaryUpload ? 'Dagboek toevoegen' : 'ECG recorder'}</Modal.Header>
            <Modal.Body>
                {props.showDownloadProgress && <HolterReadDownloadProgress downloadProgress={props.downloadProgress} />}
                {!diarySelectionAlreadyMade && (
                    <HolterDiaryUpload
                        diaryCreationFailed={diaryCreationFailed}
                        diaryData={diaryData}
                        errors={errors}
                        hideHeader={props.isDiaryUpload}
                        processActivityId={props.processActivityId}
                        processId={props.processId}
                        recorderType={props.recorderType}
                        requestId={props.requestId}
                        setDiaryCreationFailed={setDiaryCreationFailed}
                        setDiaryData={setDiaryData}
                        setErrors={setErrors}
                        setIsUploading={setIsUploading}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    color="primary"
                    isDisabled={isBlocked()}
                    isLoading={isSaving}
                    onClick={async () => {
                        if (!diarySelectionAlreadyMade) {
                            await saveDiaryData();
                        } else {
                            await props.onClose();
                        }
                    }}
                    size="medium"
                >
                    Ga verder
                </Button>
            </Modal.Footer>
        </Modal>
    );

    function isBlocked() {
        return !diarySelectionAlreadyMade && (isSaving || !errors || isUploading);
    }

    async function saveDiaryData() {
        if (diaryData.diaryType === 'uploadDiaryLater') {
            await props.onClose();

            return;
        }

        const { errors: updatedErrors, item } = validate(diarySchema, diaryData);
        setErrors(updatedErrors);
        if (errors.size > 0 || !item) {
            return;
        }

        setIsSaving(true);
        setDiaryCreationFailed(false);
        let processActivityId: string | undefined;

        if (props.requestId) {
            await createHolterDiaryForProcedure(props.requestId, item);
        } else if (props.processActivityId) {
            const action = await diaryUploadActivity({ processActivityId: props.processActivityId });
            if (!action?.activities?.[0].id) {
                setDiarySelectionAlreadyMade(true);

                return;
            }
            processActivityId = await createHolterDiaryForProcess(action.processId, item);
        } else if (props.processId) {
            processActivityId = await createHolterDiaryForProcess(props.processId, item);
        }
        if (diaryData.diaryType === DiaryTypes.paperUpload) {
            await waitForDiaryCreation(processActivityId);
        } else {
            await props.onClose();
        }
    }

    async function waitForDiaryCreation(processActivityId?: string) {
        // Converting and merging the files into a single PDF document is an asynchronous process, so we'll
        // keep polling for the specified period to see if the diary has been created before closing this modal.
        // When creation did not finish within this period, we'll assume something went wrong and will allow
        // the user to try again. There is a small possibility that the initial upload succeeds after
        // this period and the user tries the upload again. This will be fine when the user uploads
        // the same files, which is the most likely scenario. But when the user uploads different files,
        // there is a small risk that the wrong diary will be stored with the procedure. But covering this
        // scenario is not so trivial, so we'll accept this small risk for now.
        const intervalMilliseconds = 1000;
        const maxWaitTimeMilliseconds = 5 * 60 * 1000;
        let currentWaitTimeMilliseconds = 0;

        while (currentWaitTimeMilliseconds < maxWaitTimeMilliseconds) {
            await sleep(intervalMilliseconds);
            currentWaitTimeMilliseconds += intervalMilliseconds;

            let isDiarySaved = false;
            if (props.requestId) {
                isDiarySaved = await getDiaryStorageNameForRequestId(props.requestId);
            } else if (processActivityId) {
                const processActivity = await getOneProcessActivity(processActivityId, abortSignal);
                isDiarySaved = processActivity.status === ProcessActivityStatus.complete;

                if (processActivity?.customControlData?.error) {
                    setDiaryCreationFailed(true);
                    setIsSaving(false);
                    setDiaryData(processActivity.customControlData as unknown as IDiaryData);
                    currentWaitTimeMilliseconds = maxWaitTimeMilliseconds;
                }
            }

            if (isDiarySaved) {
                await props.onClose();

                return;
            }
        }
        setIsSaving(false);
        setDiaryCreationFailed(true);
    }

    async function getDiaryStorageNameForRequestId(requestId: string) {
        const procedure = await getProcedure(requestId);

        return !!procedure?.customControlData?.diaryStorageName;
    }

    async function checkDiarySelectionRequestId(requestId: string) {
        const procedure = await getProcedure(requestId);
        if (procedure?.customControlData?.diaryType) {
            setDiarySelectionAlreadyMade(true);
        }
    }

    async function getProcedure(requestId: string) {
        return (await fetchJson({ method: 'GET', url: `/api/requests/${requestId}/procedure` })).json;
    }

    async function diaryUploadActivity({
        processActivityId,
        processId,
    }: {
        processActivityId?: string;
        processId?: string;
    }) {
        let availableActions: IProcessAction[] = [];

        if (processActivityId) {
            const processActivity = await getOneProcessActivity(processActivityId, abortSignal);
            availableActions = await getAvailableActions(
                processActivity.process.id,
                { includePaused: true },
                abortSignal
            );
        } else if (processId) {
            availableActions = await getAvailableActions(processId, { includePaused: true }, abortSignal);
        }

        const processAction = availableActions.find((action) => action.name === holterStepNames.holterDiaryUpload);
        if (!processAction) {
            setDiarySelectionAlreadyMade(true);
        }

        return processAction;
    }
}
